import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useLanguage, type Language } from '@/context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

type Props = {
  /** When true, trigger stays light for transparent hero nav */
  light?: boolean;
};

const LanguageSwitcher: React.FC<Props> = ({ light = false }) => {
  const { language, setLanguage, languages } = useLanguage();
  const current = languages.find((l) => l.code === language) ?? languages[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
            light
              ? 'text-white hover:bg-white/10'
              : 'text-foreground hover:bg-muted'
          )}
          aria-label="Language"
        >
          <span
            className="h-6 w-6 rounded-full bg-cover bg-center border border-white/30 shrink-0"
            style={{ backgroundImage: `url('${current.flag}')` }}
            aria-hidden
          />
          <ChevronDown className="h-3.5 w-3.5 opacity-80" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px] bg-background border-border shadow-elevated">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code as Language)}
            className={cn(
              'cursor-pointer gap-2',
              language === lang.code && 'bg-muted font-semibold'
            )}
          >
            <span
              className="h-5 w-5 rounded-full bg-cover bg-center border border-border shrink-0"
              style={{ backgroundImage: `url('${lang.flag}')` }}
              aria-hidden
            />
            <span>{lang.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
